import { history, useIntl, useLocation } from '@umijs/max';
import React from 'react';
import styled from 'styled-components';

const IconMcpUnplug: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m19 5 3-3" />
    <path d="m2 22 3-3" />
    <path d="M6.3 20.3a2.4 2.4 0 0 0 3.4 0L12 18l-6-6-2.3 2.3a2.4 2.4 0 0 0 0 3.4Z" />
    <path d="M7.5 13.5 10 11" />
    <path d="M10.5 16.5 13 14" />
    <path d="m12 6 6 6 2.3-2.3a2.4 2.4 0 0 0 0-3.4l-2.6-2.6a2.4 2.4 0 0 0-3.4 0Z" />
  </svg>
);


/** Lucide puzzle — Skill Hub */
const IconSkillPuzzle: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15.39 4.39a1 1 0 0 0 1.68-.474 2.5 2.5 0 1 1 3.014 3.015 1 1 0 0 0-.474 1.68l1.683 1.682a2.414 2.414 0 0 1 0 3.414L19.61 15.39a1 1 0 0 1-1.68-.474 2.5 2.5 0 1 0-3.014 3.015 1 1 0 0 1 .474 1.68l-1.683 1.682a2.414 2.414 0 0 1-3.414 0L8.61 19.61a1 1 0 0 0-1.68.474 2.5 2.5 0 1 1-3.014-3.015 1 1 0 0 0 .474-1.68l-1.683-1.682a2.414 2.414 0 0 1 0-3.414L4.39 8.61a1 1 0 0 1 1.68.474 2.5 2.5 0 1 0 3.014-3.015 1 1 0 0 1-.474-1.68l1.683-1.682a2.414 2.414 0 0 1 3.414 0z" />
  </svg>
);

/** GitHub — Open Source */
const IconOpenSource: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
  </svg>
);

const TabList = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

const HeaderAlign = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 0 12px;
  box-sizing: border-box;
`;

/** 顶栏 83px 内：白字 + 图标，样式参考稿图仅作视觉，仍为两项切换 */
const HubTab = styled.button<{ $active: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 8px 14px;
  font-size: 14px;
  line-height: 1.3;
  cursor: pointer;
  outline: none;
  font-family: inherit;
  box-sizing: border-box;
  border: none;
  border-radius: 8px;
  margin-right: 30px;
  min-width:80px;
  justify-content: center;
  background: ${({ $active }) => ($active ? 'rgba(255,255,255,0.14)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : 'rgba(255,255,255,0.88)')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  transition:
    color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;

  .hub-tab-icon {
    display: flex;
    align-items: center;
    line-height: 0;
    color: inherit;
  }

  .hub-tab-icon svg {
    display: block;
    flex-shrink: 0;
  }

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.12);
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.35);
  }
`;

const HubHeaderSegment: React.FC = () => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const isSkill = pathname.startsWith('/skill');
  const isOpensource = pathname.startsWith('/opensource');

  const mcpLabel = intl.formatMessage({ id: 'pages.hub.mcpServer' });
  const skillLabel = intl.formatMessage({ id: 'pages.hub.skillHub' });
  const ossLabel = intl.formatMessage({ id: 'pages.hub.opensource' });

  return (
    <HeaderAlign>
      <TabList role="tablist" aria-label={intl.formatMessage({ id: 'pages.hub.title' })}>
      <HubTab
          type="button"
          role="tab"
          aria-selected={!isSkill}
          $active={!isSkill}
          onClick={() => history.push('/serviceOverview')}
        >
          {/* <span className="hub-tab-icon" aria-hidden>
            <IconMcpUnplug />
          </span> */}
          <span>{mcpLabel}</span>
        </HubTab>

      {/* <HubTab
          type="button"
          role="tab"
          aria-selected={isOpensource}
          $active={isOpensource}
          onClick={() => history.push('/opensource')}
        >
          <span className="hub-tab-icon" aria-hidden>
            <IconOpenSource />
          </span>
          <span>{ossLabel}</span>
        </HubTab> */}
        <HubTab
          type="button"
          role="tab"
          aria-selected={isSkill}
          $active={isSkill}
          onClick={() => history.push('/skill/plaza')}
        >
          {/* <span className="hub-tab-icon" aria-hidden>
            <IconSkillPuzzle />
          </span> */}
          <span>{skillLabel}</span>
        </HubTab>
       
      </TabList>
    </HeaderAlign>
  );
};

export function isSkillHubPath(path: string): boolean {
  return path.startsWith('/skill');
}

export function isOpensourcePath(path: string): boolean {
  return path.startsWith('/opensource');
}

export { HubHeaderSegment };
